import React, { useEffect, useState, useRef, useCallback } from "react"; // Ensure useEffect is imported
import PropTypes from "prop-types";
import { FaEdit, FaSave, FaTimes } from "react-icons/fa";
import communityProfileService from "../../service/center/communityProfileService"; // Adjusted path
import { auth } from "../../firebase"; // Corrected path
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Sort,
  Filter,
  Edit,
  Toolbar,
  CommandColumn,
  Inject,
  ColumnMenu,
} from "@syncfusion/ej2-react-grids";
import { TextBoxComponent } from "@syncfusion/ej2-react-inputs";
import { DialogComponent } from "@syncfusion/ej2-react-popups";
import {
  DropDownListComponent,
  CheckBoxSelection,
} from "@syncfusion/ej2-react-dropdowns";
import { USStates } from "./../datasource";
import countiesData from "../../counties_list.json"; // Import counties data
import { MultiSelectComponent } from "@syncfusion/ej2-react-dropdowns";
import {
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  doc,
  setDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { UserStatus } from "../../utils/enums";
import CustomSwitch from "../../../src/components/Switch";
import { useForm } from "react-hook-form"; // Add this import
import { updateDoc } from "firebase/firestore";
import helpers from "../../../src/utils/helpers";

const CommunityProfile = ({ className = "" }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [tempFormData, setTempFormData] = useState({});
  const [lastEditedTime, setLastEditedTime] = useState(null);
  const [data, setData] = useState(null);
  const [userId, setUserId] = useState(null); // State to hold user ID
  const gridStaff = useRef(null);
  const [staff, setStaff] = useState([]);
  const govConactsRef = useRef(null);
  const [govContacts, setGovContacts] = useState([]);
  const [a501c3, seta501c3] = useState(null);
  const [counties, setCounties] = useState([]); // State to hold counties
  const [selectedState, setSelectedState] = useState("");
  const [missingFields, setMissingFields] = useState([]);
  const [availableCounties, setAvailableCounties] = useState([]);
  const [isLoadingCounties, setIsLoadingCounties] = useState(false);
  const { control, reset } = useForm({
    defaultValues: {
      immediate_resp: false,
      assistance_log: false,
      dmg_assessment: false,
    },
  });
  const [viewOnlyAccess, setViewOnlyAccess] = useState({
    immediate_resp: false,
    assistance_log: false,
    dmg_assessment: false,
  });

  const employeeDetails = JSON.parse(sessionStorage.getItem("employeeDetails"));

  const donationOptions = [
    { text: "Monetary", value: "monetary" },
    { text: "Food", value: "food" },
    { text: "Water", value: "water" },
    { text: "Hygiene Products", value: "hygiene" },
    { text: "Sanitation Products", value: "sanitation" },
    { text: "Clothing", value: "clothing" },
    { text: "Furniture", value: "furniture" },
    { text: "Medical Supplies", value: "medical" },
    { text: "Household Supplies", value: "household" },
    { text: "Childcare Items", value: "childcare" },
    { text: "Pet Supplies", value: "pet" },
    { text: "Tools and Equipment", value: "tools" },
    { text: "Personal Comfort Items", value: "comfort" },
    { text: "School Supplies", value: "school" },
    { text: "Other", value: "other" },
  ];

  const [requiredFields, setRequiredFields] = useState([
    "name_of_hub",
    "contact_person",
    "mobile_number",
    "first_name",
    "last_name",
    "address1",
    "city",
    "state",
    "county",
    "zip",
    "email",
    "website_url",
    "soc_med",
    "other_info",
    "a501c3",
    "contact_email",
    "contact_phone",
  ]);
  const selectedUserId = sessionStorage.getItem("userId");

  useEffect(() => {
    const fetchUserId = () => {
      const user = auth.currentUser; // Get the current user
      if (user && selectedUserId) {
        setUserId(selectedUserId); // Set the user ID
      }
    };

    fetchUserId();
  }, []);

  const fetchData = useCallback(async () => {
    if (userId) {
      const userData = await communityProfileService.getCommunityProfileByRole(
        "users",
        userId
      );

      const profileData =
        await communityProfileService.getCommunityProfileByRole(
          "centers",
          userData.center_id
        );

      setData(profileData);
      setTempFormData(profileData);
      if (profileData.a501c3) seta501c3(profileData.a501c3);
      if (profileData.first_name) {
        setTempFormData({
          ...profileData,
          contact_person: `${profileData.first_name} ${profileData.last_name}`,
        });
      }

      if (profileData.state) {
        const selectedState = profileData.state;
        const getState = USStates.find(
          (s) => s.abbreviation === selectedState
        ).name;
        const filteredCounties = countiesData
          .filter((cd) => cd.State === getState)
          .map((cd) => cd.County);
        setCounties(filteredCounties);
        setSelectedState(selectedState);
      }

      const staffData = await communityProfileService.getStaff(userId);
      setStaff(staffData.filter((s) => s.is_deleted !== true));

      const govData = await communityProfileService.getGov(userId);
      setGovContacts(govData.filter((g) => g.is_deleted !== true));
    }
  }, [userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data?.view_only_access_to) {
      const accessData = {
        immediate_resp: data.view_only_access_to.immediate_resp ?? false,
        assistance_log: data.view_only_access_to.assistance_log ?? false,
        dmg_assessment: data.view_only_access_to.dmg_assessment ?? false,
      };
      setViewOnlyAccess(accessData);
      reset(accessData);
    }
  }, [data, reset]);

  const handleViewOnlyAccessChange = async (field, event) => {
    try {
      // Create new access object that preserves previous values
      const newAccess = {
        immediate_resp:
          field === "immediate_resp"
            ? event.target.value
            : viewOnlyAccess.immediate_resp,
        assistance_log:
          field === "assistance_log"
            ? event.target.value
            : viewOnlyAccess.assistance_log,
        dmg_assessment:
          field === "dmg_assessment"
            ? event.target.value
            : viewOnlyAccess.dmg_assessment,
        lockbox:
          field === "lockbox" ? event.target.value : viewOnlyAccess.lockbox,
      };

      // Set the entire object directly
      const centerRef = doc(db, "centers", data.id);
      await setDoc(
        centerRef,
        { view_only_access_to: newAccess },
        { merge: true }
      );

      // Update local state with boolean values
      console.log("New access:", newAccess);
      setViewOnlyAccess(newAccess);
    } catch (error) {
      console.error("Error updating view only access:", error);
      setViewOnlyAccess((prev) => ({
        ...prev,
        [field]: !event.target.value,
      }));
      reset(viewOnlyAccess);
    }
  };

  const startEditing = (event) => {
    event.preventDefault();
    setTempFormData({ ...data });
    setIsEditMode(true);
  };

  const saveChanges = async (event) => {
    event.preventDefault();
    const currentTime = new Date();

    // Create a copy of tempFormData without the view_only_access_to field
    const { view_only_access_to, ...dataToSave } = tempFormData;
    let updatedData = {
      ...dataToSave,
      updated_at: currentTime,
      email: helpers.lowerCaseString(tempFormData.email),
    };

    // Validation checks
    const missing = requiredFields.filter(
      (field) =>
        !updatedData[field] ||
        (typeof updatedData[field] === "string" &&
          updatedData[field].trim() === "")
    );

    if (missing.length > 0) {
      setMissingFields(missing);
      console.log("Missing required fields:", missing);
      return;
    }

    setMissingFields([]);

    try {
      // Check if address fields have changed
      const addressChanged =
        data.address1 !== updatedData.address1 ||
        data.city !== updatedData.city ||
        data.state !== updatedData.state ||
        data.zip !== updatedData.zip;

      if (addressChanged) {
        console.log("Address changed");
        // Construct address string
        const addressString = `${updatedData.address1}, ${updatedData.city}, ${updatedData.state} ${updatedData.zip}`;

        try {
          // Get coordinates from Google Geocoding API
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
              addressString
            )}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
          );

          const geocodeData = await response.json();

          if (geocodeData.results && geocodeData.results[0]) {
            const { lat, lng } = geocodeData.results[0].geometry.location;
            updatedData = {
              ...updatedData,
              latitude: lat,
              longitude: lng,
            };
            console.log(`Updated coordinates: ${lat}, ${lng}`);
          }
        } catch (geocodeError) {
          console.error("Error geocoding address:", geocodeError);
          // Continue with save even if geocoding fails
        }
      }

      // If county has changed
      if (data.county !== updatedData.county) {
        // First, update citizens in the old county to remove this center_id
        const oldCitizensQuery = query(
          collection(db, "users"),
          where("user_role", "==", "citizen"),
          where("county", "==", data.county),
          where("state", "==", data.state),
          where("center_id", "==", data.id)
        );

        const oldCitizensSnapshot = await getDocs(oldCitizensQuery);

        // Batch update for old citizens
        const oldBatch = writeBatch(db);
        oldCitizensSnapshot.docs.forEach((doc) => {
          oldBatch.update(doc.ref, {
            center_id: null,
          });
        });
        await oldBatch.commit();

        // Then, update citizens in the new county
        const newCitizensQuery = query(
          collection(db, "users"),
          where("user_role", "==", "citizen"),
          where("county", "==", updatedData.county),
          where("state", "==", updatedData.state)
        );

        const newCitizensSnapshot = await getDocs(newCitizensQuery);

        // Batch update for new citizens
        const newBatch = writeBatch(db);
        newCitizensSnapshot.docs.forEach((doc) => {
          newBatch.update(doc.ref, {
            center_id: data.id,
          });
        });
        await newBatch.commit();

        console.log(
          `Updated citizens for county change from ${data.county} to ${updatedData.county}`
        );
      }

      // Proceed with normal profile updates
      await communityProfileService.updateCommunityProfile("users", userId);

      //update in users collection
      await updateDoc(doc(db, "users", selectedUserId), {
        email: tempFormData.email.toLowerCase(),
      });

      if (data.id) {
        const { status, ...dataWithoutStatus } = updatedData;
        await communityProfileService.updateCommunityProfile(
          "centers",
          data.id,
          dataWithoutStatus
        );
      }
      // Check if all required fields are filled
      const isProfileComplete = requiredFields.every(
        (field) => updatedData[field]
      );
      if (isProfileComplete) {
        console.log("Profile is complete");
        await communityProfileService.updateCommunityProfile("users", userId, {
          status: UserStatus.ACTIVE,
        });
      }

      // Update local state while preserving view_only_access_to
      setData({
        ...updatedData,
      });
      setIsEditMode(false);
      setLastEditedTime(currentTime);
    } catch (error) {
      console.error("Error updating profile: ", error);
    }
  };

  const cancelEditing = (event) => {
    event.preventDefault();
    setTempFormData({ ...data });
    setIsEditMode(false);
    setMissingFields([]);
  };

  const handleInputBlur = async (event) => {
    const currentTime = new Date();
    const updatedData = {
      ...tempFormData,
      updated_at: currentTime,
    };

    try {
      await communityProfileService.updateCommunityProfile(
        "centers",
        data.id,
        updatedData
      );
      setData(updatedData);
      setLastEditedTime(currentTime);
    } catch (error) {
      console.error("Error updating profile: ", error);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setTempFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (name === "a501c3") {
      seta501c3(value);
    }
  };

  const handleKeyDown = (e) => {
    const allowedKeys = [
      "Backspace",
      "Tab",
      "Enter",
      "Shift",
      "ArrowLeft",
      "ArrowRight",
      "Space",
    ];

    if (!/^[a-zA-Z\s]$/.test(e.key) && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleKeyDownAddress = (e) => {
    const allowedKeys = [
      "Backspace",
      "Tab",
      "Enter",
      "Shift",
      "ArrowLeft",
      "ArrowRight",
      "Space",
      "-",
      "#",
      ".",
      ",",
    ];

    if (!/^[a-zA-Z0-9\s,.\-#]$/.test(e.key) && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleAddStaff = () => {
    if (gridStaff.current) {
      gridStaff.current.addRecord();
    }
  };

  const handleAddGov = () => {
    if (govConactsRef.current) {
      govConactsRef.current.addRecord();
    }
  };

  const generateUniqueID = () => {
    return "_" + Math.random().toString(36).substr(2, 9);
  };

  const sortSettings = {
    allowSorting: true,
  };
  const filterSettings = {
    type: "CheckBox",
  };
  const columnMenuItems = ["SortAscending", "SortDescending", "Filter"];

  const pageSettings = { pageSize: 10 };

  const yesNoOptions = [
    { text: "Yes", value: "Yes" },
    { text: "No", value: "No" },
  ];

  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const isNewlyRegistered = localStorage.getItem("newlyRegistered");
    if (isNewlyRegistered === "true") {
      setShowPopup(true);
      localStorage.removeItem("newlyRegistered"); // Remove the flag after showing the popup
    }
  }, []);

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const dialogContent = () => {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <h2
          style={{
            fontSize: "24px",
            fontWeight: "normal",
            color: "#1e1919",
            marginBottom: "10px",
          }}
        >
          Welcome to your Center Profile!
        </h2>
        <p style={{ fontSize: "16px", color: "#637381", marginBottom: "20px" }}>
          Please finish your profile to get the most out of our services.
        </p>
        <button
          onClick={handleClosePopup}
          className="btn-default"
          style={{
            padding: "10px 20px",
            cursor: "pointer",
          }}
        >
          Got it!
        </button>
      </div>
    );
  };

  const actionBeginStaff = async (args) => {
    if (args.requestType === "save") {
      if (args.action === "add") {
        // Ensure staffId is set and status is true
        const newStaffData = {
          ...args.data,
          staffId: args.data.staffId || generateUniqueID(),
          is_deleted: false, // Set status to true when adding
        };
        console.log("Adding new staff:", newStaffData);
        await communityProfileService.addStaff(userId, newStaffData);
      } else if (args.action === "edit") {
        await communityProfileService.updateStaff(
          userId,
          args.data.id,
          args.data
        );
      }
      fetchData(); // Refresh the data after adding or editing
    } else if (args.requestType === "delete") {
      // Set status to false when deleting
      const updatedData = {
        ...args.data[0],
        is_deleted: true,
      };
      await communityProfileService.updateStaff(
        userId,
        updatedData.id,
        updatedData
      );
      fetchData();
      //setDisastersLastEditedTime(new Date());
    } else if (args.requestType === "beginEdit" || args.requestType === "add") {
      setTimeout(() => {
        if (args.dialog) {
          // Set the dialog header based on the action
          if (args.requestType === "beginEdit") {
            args.dialog.header = "Edit Staff Info";
          } else {
            args.dialog.header = "Add Staff Info";
          }

          // Set the dialog height
          args.dialog.element.style.maxHeight = "900px"; // Set max height
          args.dialog.element.style.height = "auto"; // Set height to auto

          // Set the header styles
          const headerContent = args.dialog.element.querySelector(
            ".e-dlg-header-content"
          );
          const header = args.dialog.element.querySelector(".e-dlg-header");
          if (headerContent) {
            headerContent.style.backgroundColor = "#348BFF";
            headerContent.style.color = "#FFFFFF";
          }
          if (header) {
            header.style.color = "#FFFFFF";
          }

          // Style the save and cancel buttons
          const saveButton = args.dialog.element.querySelector(".e-primary");
          const cancelButton = args.dialog.element.querySelector(
            ".e-btn:not(.e-primary)"
          );

          if (saveButton) {
            saveButton.style.backgroundColor = "#FFFFFF";
            saveButton.style.color = "#348BFF";
            saveButton.style.border = "none";
          }

          if (cancelButton) {
            cancelButton.style.backgroundColor = "#FFFFFF";
            cancelButton.style.color = "#348BFF";
            cancelButton.style.border = "1px solid #348BFF";
          }
        }
      }, 0); // Delay execution to allow dialog to initialize
    }
  };

  const actionBeginGov = async (args) => {
    if (args.requestType === "save") {
      if (args.action === "add") {
        // Ensure govId is set and status is true
        const newGovData = {
          ...args.data,
          govId: args.data.govId || generateUniqueID(),
          is_deleted: false, // Set status to true when adding
        };
        console.log("Adding new gov:", newGovData);
        await communityProfileService.addGov(userId, newGovData);
      } else if (args.action === "edit") {
        await communityProfileService.updateGov(
          userId,
          args.data.id,
          args.data
        );
      }
      fetchData(); // Refresh the data after adding or editing
    } else if (args.requestType === "delete") {
      // Set status to false when deleting
      const updatedData = {
        ...args.data[0],
        is_deleted: true,
      };
      await communityProfileService.updateGov(
        userId,
        updatedData.id,
        updatedData
      );
      fetchData();
    } else if (args.requestType === "beginEdit" || args.requestType === "add") {
      setTimeout(() => {
        if (args.dialog) {
          // Set the dialog header based on the action
          if (args.requestType === "beginEdit") {
            args.dialog.header = "Edit Government Contact Info";
          } else {
            args.dialog.header = "Add Government Contact Info";
          }

          // Set the dialog height
          args.dialog.element.style.maxHeight = "900px"; // Set max height
          args.dialog.element.style.height = "auto"; // Set height to auto

          // Set the header styles
          const headerContent = args.dialog.element.querySelector(
            ".e-dlg-header-content"
          );
          const header = args.dialog.element.querySelector(".e-dlg-header");
          if (headerContent) {
            headerContent.style.backgroundColor = "#348BFF";
            headerContent.style.color = "#FFFFFF";
          }
          if (header) {
            header.style.color = "#FFFFFF";
          }

          // Style the save and cancel buttons
          const saveButton = args.dialog.element.querySelector(".e-primary");
          const cancelButton = args.dialog.element.querySelector(
            ".e-btn:not(.e-primary)"
          );

          if (saveButton) {
            saveButton.style.backgroundColor = "#FFFFFF";
            saveButton.style.color = "#348BFF";
            saveButton.style.border = "none";
          }

          if (cancelButton) {
            cancelButton.style.backgroundColor = "#FFFFFF";
            cancelButton.style.color = "#348BFF";
            cancelButton.style.border = "1px solid #348BFF";
          }
        }
      }, 0); // Delay execution to allow dialog to initialize
    }
  };

  const onShowRequiredFields = (field) => {
    // Define your logic to check if a field is required
    return requiredFields.includes(field);
  };

  useEffect(() => {
    // Filter counties based on the selected state
    if (selectedState) {
      const getState = USStates.find(
        (s) => s.abbreviation === selectedState
      )?.name;
      const filteredCounties = countiesData
        .filter((cd) => cd.State === getState)
        .map((cd) => ({ County: cd.County })); // Adjusted to match the expected format
      setCounties(filteredCounties);
    }
  }, [selectedState]);

  const handleStateChange = (e) => {
    const stateAbbreviation = e.value;
    setSelectedState(stateAbbreviation);
    setTempFormData({ ...tempFormData, state: stateAbbreviation, county: "" }); // Reset county when state changes
    fetchAvailableCounties(stateAbbreviation);
  };

  const fetchAvailableCounties = async (stateAbbr) => {
    if (!stateAbbr) {
      setAvailableCounties([]);
      return;
    }

    setIsLoadingCounties(true);
    try {
      // Get the state name first
      const stateObject = USStates.find((s) => s.abbreviation === stateAbbr);
      const stateName = stateObject ? stateObject.name : "";
      if (!stateName) {
        setAvailableCounties([]);
        return;
      }

      // Query centers in the selected state
      const centersRef = collection(db, "centers");
      const q = query(centersRef, where("state", "==", stateAbbr));
      const snapshot = await getDocs(q);

      // Get taken counties, excluding the current center's county
      const takenCounties = snapshot.docs
        .filter((doc) => doc.id !== data.id)
        .map((doc) => doc.data().county)
        .filter((county) => county);

      // First, get all counties for the selected state
      const stateCounties = countiesData
        .filter((county) => county.State === stateName)
        .map((county) => ({ County: county.County }));

      // Then filter out taken counties
      const availableCounties = stateCounties.filter(
        (county) => !takenCounties.includes(county.County)
      );

      // If this center already has a county in this state, add it to available options
      if (data.county && data.state === stateAbbr) {
        const currentCounty = { County: data.county };
        if (!availableCounties.find((c) => c.County === data.county)) {
          availableCounties.unshift(currentCounty);
        }
      }

      setAvailableCounties(availableCounties);
    } catch (error) {
      console.error("Error fetching available counties:", error);
      // Fallback to showing all counties of the selected state
      const stateObject = USStates.find((s) => s.abbreviation === stateAbbr);
      const stateName = stateObject ? stateObject.name : "";
      const stateCounties = countiesData
        .filter((county) => county.State === stateName)
        .map((county) => ({ County: county.County }));
      setAvailableCounties(stateCounties);
    } finally {
      setIsLoadingCounties(false);
    }
  };

  useEffect(() => {
    if (selectedState) {
      fetchAvailableCounties(selectedState);
    }
  }, [selectedState]);

  const renderField = (label, name, required, type) => {
    return (
      <div className="flex w-full flex-col items-start justify-start gap-1">
        <div className="text-gray-600 relative text-left text-xs leading-[20px]">
          {label}
          {required && (
            <p className="absolute right-[-15px] top-[4px] text-[26px] text-red-500">
              *
            </p>
          )}
        </div>
        <div
          className={`box-border flex h-10 flex-row items-center justify-between self-stretch`}
        >
          {name === "state" ? (
            <DropDownListComponent
              readonly={!isEditMode}
              dataSource={USStates}
              fields={{ text: "name", value: "abbreviation" }}
              style={{ backgroundColor: isEditMode ? "" : "#f3f4f6" }}
              floatLabelType="Auto"
              cssClass="e-outline"
              change={(e) => {
                setTempFormData({ ...tempFormData, state: e.value });
                handleStateChange(e); // Call handleStateChange to update counties
              }}
              value={tempFormData.state || ""}
            />
          ) : name === "county" ? (
            <DropDownListComponent
              readonly={!isEditMode}
              floatLabelType="Auto"
              dataSource={availableCounties}
              fields={{ text: "County", value: "County" }}
              value={tempFormData.county || ""}
              style={{ backgroundColor: isEditMode ? "" : "#f3f4f6" }}
              onChange={(e) => {
                setTempFormData({
                  ...tempFormData,
                  county: e.value,
                });
              }}
              cssClass="e-outline"
              enabled={!!selectedState && !isLoadingCounties}
              placeholder={isLoadingCounties ? "Loading counties..." : ""}
            />
          ) : name === "a501c3" ? (
            <DropDownListComponent
              readonly={!isEditMode}
              dataSource={yesNoOptions}
              fields={{ text: "text", value: "value" }}
              style={{ backgroundColor: isEditMode ? "" : "#f3f4f6" }}
              floatLabelType="Auto"
              cssClass="e-outline"
              change={(e) => {
                setTempFormData({ ...tempFormData, a501c3: e.value });
                handleInputBlur();
              }}
              blur={handleInputBlur}
              value={tempFormData.a501c3 || ""}
            />
          ) : name === "accept_donations" ? (
            ["Yes", "No"].map((option) => (
              <label className="flex items-center" key={option}>
                <input
                  type="radio"
                  name="accept_donations"
                  value={option}
                  checked={tempFormData.accept_donations === option}
                  onChange={() =>
                    setTempFormData({
                      ...tempFormData,
                      accept_donations: option,
                    })
                  }
                  disabled={!isEditMode}
                />
                <span className="ml-2">{option}</span>
              </label>
            ))
          ) : name === "donation_types" ? (
            <MultiSelectComponent
              id="donation_types"
              dataSource={donationOptions}
              fields={{ text: "text", value: "value" }}
              placeholder="Select donation types"
              mode="CheckBox"
              showSelectAll={true}
              showDropDownIcon={true}
              popupHeight="350px"
              cssClass={`e-outline ${isEditMode ? "" : "disabled-multiselect"}`}
              readonly={!isEditMode}
              value={tempFormData.donation_types || []}
              change={(e) => {
                setTempFormData({
                  ...tempFormData,
                  donation_types: e.value,
                });
              }}
            >
              <Inject services={[CheckBoxSelection]} />
            </MultiSelectComponent>
          ) : (
            <TextBoxComponent
              onKeyDown={
                name === "name_of_hub"
                  ? undefined
                  : name === "mobile_number" || name === "contact_phone"
                  ? (e) => {
                      if (
                        !/^[0-9+()-]$/.test(e.key) &&
                        ![
                          "Backspace",
                          "Tab",
                          "Enter",
                          "ArrowLeft",
                          "ArrowRight",
                        ].includes(e.key)
                      ) {
                        e.preventDefault();
                      }
                    }
                  : name === "email" ||
                    name === "contact_email" ||
                    name === "website_url"
                  ? (e) => {
                      if (
                        !/^[a-zA-Z0-9@._-]$/.test(e.key) &&
                        ![
                          "Backspace",
                          "Tab",
                          "Enter",
                          "ArrowLeft",
                          "ArrowRight",
                        ].includes(e.key)
                      ) {
                        e.preventDefault();
                      }
                    }
                  : name === "address1" || name === "address2"
                  ? handleKeyDownAddress
                  : name === "zip"
                  ? undefined
                  : handleKeyDown
              }
              floatLabelType="Auto"
              id={name}
              type={type}
              name={name}
              cssClass="e-outline"
              value={
                name === "contact_person"
                  ? `${tempFormData.first_name || ""} ${
                      tempFormData.last_name || ""
                    }`
                  : tempFormData[name] || ""
              }
              onChange={handleInputChange}
              readOnly={!isEditMode}
              blur={handleInputBlur}
              style={{ backgroundColor: isEditMode ? "" : "#f3f4f6" }}
            />
          )}
        </div>
        {missingFields.includes(name) && (
          <p className="mt-1 text-xs text-red-500">Required Field</p>
        )}
        {name === "county" &&
          availableCounties.length === 0 &&
          selectedState &&
          !isLoadingCounties && (
            <p className="mt-1 text-xs text-yellow-500">
              No available counties in this state
            </p>
          )}
      </div>
    );
  };

  const renderViewOnlyAccess = () => {
    const switchProps = {
      control,
      rowType: true,
      onColor: isEditMode ? "#0A8525" : "#8F8F8F",
      width: 42,
      height: 24,
      handleDiameter: 16,
      errors: {},
      disabled: !isEditMode,
    };

    const accessItems = [
      {
        name: "immediate_resp",
        label: "Immediate Response",
      },
      {
        name: "assistance_log",
        label: "Assistance Log",
      },
      {
        name: "dmg_assessment",
        label: "Damage Assessment",
      },
      {
        name: "lockbox",
        label: "Lockbox",
      },
    ];

    return (
      <div
        className={`flex flex-col gap-4 self-stretch text-sm text-[#242424] ${
          !isEditMode ? "pointer-events-none" : ""
        }`}
      >
        {accessItems.map(({ name, label }) => (
          <div
            key={name}
            className="flex flex-row items-center justify-between font-normal"
          >
            <p className="font-medium">
              {label}{" "}
              <span className="font-normal text-[#525252]">
                ({viewOnlyAccess[name] ? "View Only" : "Read and Write"})
              </span>
            </p>
            <CustomSwitch
              {...switchProps}
              name={name}
              rules={{
                onChange: (e) =>
                  isEditMode && handleViewOnlyAccessChange(name, e),
              }}
              className={`${!isEditMode ? "opacity-50" : ""}`}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <form
      className={`m-0 box-border flex w-[1648px] max-w-full flex-col items-start justify-start gap-2.5 px-2.5 py-[15px] leading-[normal] tracking-[normal] ${className}`}
    >
      <div id="dialog-container"></div>
      <div className="flex flex-row items-start justify-start gap-1.5 py-0 pl-0 pr-5">
        <a className="relative inline-block min-w-[77px] text-left  text-xs font-medium leading-[16px] text-dodgerblue [text-decoration:none]">
          Center Profile
        </a>
      </div>
      <section className="flex max-w-full flex-col items-start justify-start self-stretch bg-white">
        <header className="mb-2 flex flex-row items-center justify-between self-stretch">
          <div>
            <h2 className="mb-1 text-sm font-bold text-blue-500">
              BLUE SKY CENTER INFORMATION
            </h2>
          </div>
          <div className="mr-3 flex items-center gap-2">
            {isEditMode ? (
              <>
                <button
                  type="button"
                  className="btn-default flex cursor-pointer items-center gap-2 px-3 py-1.5"
                  onClick={saveChanges}
                >
                  <FaSave className="h-3 w-3" /> Save
                </button>
                <button
                  type="button"
                  className="btn-cancel flex cursor-pointer  gap-2 px-3 py-1.5"
                  onClick={cancelEditing}
                >
                  <FaTimes className="h-3 w-3" /> Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn-default flex cursor-pointer gap-1 px-3 py-1.5"
                onClick={startEditing}
              >
                <FaEdit className="h-3 w-3" />
                Edit
              </button>
            )}
          </div>
        </header>
        <div className="mr-3 flex flex-col gap-4 self-stretch">
          {data && renderField("Name of Center", "name_of_hub", true)}
          <div className="flex gap-2">
            {data &&
              renderField("Contact Person First Name", "first_name", true)}
            {data && renderField("Contact Person Last Name", "last_name", true)}
          </div>
          {data && renderField("Contact Number", "mobile_number", true)}
          {data &&
            renderField(
              "Address 1",
              "address1",
              true,
              "e.g., 123 Main St, Springfield, IL 62704"
            )}
          {data &&
            renderField(
              "Address 2",
              "address2",
              false,
              "e.g., 123 Main St, Springfield, IL 62704"
            )}
          <div className="flex gap-2">
            {data && renderField("City", "city", true)}
            {data && renderField("State", "state", true)}
            {data && renderField("Zip", "zip", true)}
            {data && renderField("County", "county", true)}
          </div>

          <div className="flex flex-row gap-4 self-stretch">
            <div className="flex-1">
              {data &&
                renderField(
                  "Email",
                  "email",
                  true,
                  "e.g., janedoe@example.com"
                )}
            </div>
            <div className="flex-1">
              {data && renderField("Website", "website_url", true)}
            </div>
          </div>
          <div className="flex flex-row gap-4 self-stretch">
            <div className="flex-1">
              {data && renderField("Social Media", "soc_med", true)}
            </div>
            <div className="flex-1">
              {data &&
                renderField("Other Information Provided", "other_info", true)}
            </div>
          </div>
          <div className="flex flex-row gap-4 self-stretch">
            <div className="flex-1">
              {data && renderField("Are you a 501C3", "a501c3", true)}
            </div>
            <div className="flex-1">
              {data &&
                a501c3 === "Yes" &&
                renderField("EIN", "ein", true, "number")}
            </div>
          </div>
          <div className="flex flex-1 space-x-4">
            {data &&
              renderField(
                "Contact Email",
                "contact_email",
                true,
                "e.g., janedoe@example.com"
              )}
            {data &&
              renderField("Contact Phone", "contact_phone", true, "number")}
          </div>
          <div className="flex flex-1 space-x-10">
            <div className="whitespace-nowrap">
              {data &&
                renderField(
                  "Do you accept donations?",
                  "accept_donations",
                  false,
                  "e.g., janedoe@example.com"
                )}
            </div>
            {data &&
              tempFormData.accept_donations === "Yes" &&
              renderField(
                "What types of donations do you accept?",
                "donation_types",
                false
              )}
          </div>
        </div>

        {employeeDetails.is_admin && (
          <div>
            <h2 className="mb-1 py-4 text-sm font-bold text-blue-500">
              EDIT ACCESS FOR CITIZEN RECOVERY TRACKER
            </h2>
            {renderViewOnlyAccess()}
          </div>
        )}
      </section>

      <div className="box-border flex max-w-full flex-col items-start justify-start gap-[10px] self-stretch rounded-3xs border-[1px] border-solid border-gray-200 bg-white px-[19px] pb-5 pt-[18px] text-left text-xs text-darkslateblue">
        <div className="flex max-w-full flex-col items-start justify-start self-stretch bg-white">
          <div
            className={`mq280:flex-wrap mb-2 flex w-[1588px] max-w-full flex-row items-start justify-between gap-5 text-left text-xs leading-[normal] tracking-[normal] text-dodgerblue ${className}`}
          >
            <div
              className={`mq280:flex-wrap flex w-[1588px] max-w-full flex-row items-start justify-between gap-5 text-left text-xs leading-[normal] tracking-[normal] text-dodgerblue ${className}`}
            >
              <div className="flex w-full flex-col items-start justify-start">
                <a className="relative inline-block w-full font-bold leading-[21px] text-[inherit] [text-decoration:none]">
                  Official Staff Contacts (At least 2)
                </a>
                <div className="relative self-stretch font-medium leading-[21px] text-silver-200">
                  {/* {data.lastUpdatedDisasters &&
                    `Last Updated: ${tempDisastersFormData.lastUpdatedDisasters}`}
                  {console.log("ahah ", data.lastUpdatedDisasters)} */}
                </div>
              </div>
              <div className="flex flex-col items-start justify-start px-0 pb-0 pt-[7px]">
                <div className="flex flex-row items-start justify-start gap-2.5">
                  <button
                    type="button"
                    className="btn-default flex cursor-pointer gap-1 px-3 py-1.5"
                    onClick={handleAddStaff}
                  >
                    <div className="flex flex-col items-start justify-start px-0 pb-0 pt-px">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          opacity="0.4"
                          d="M14.0672 6.39191H13.2651V5.60774C13.2651 5.27296 12.9966 5 12.6658 5C12.3357 5 12.0665 5.27296 12.0665 5.60774V6.39191H11.2658C10.935 6.39191 10.6665 6.66487 10.6665 6.99965C10.6665 7.33443 10.935 7.6074 11.2658 7.6074H12.0665V8.39226C12.0665 8.72704 12.3357 9 12.6658 9C12.9966 9 13.2651 8.72704 13.2651 8.39226V7.6074H14.0672C14.3973 7.6074 14.6665 7.33443 14.6665 6.99965C14.6665 6.66487 14.3973 6.39191 14.0672 6.39191Z"
                          fill="white"
                        />
                        <path
                          d="M6.33325 10.0107C3.63607 10.0107 1.33325 10.442 1.33325 12.1648C1.33325 13.887 3.62205 14.3337 6.33325 14.3337C9.02977 14.3337 11.3333 13.9025 11.3333 12.1797C11.3333 10.4568 9.04445 10.0107 6.33325 10.0107Z"
                          fill="white"
                        />
                        <path
                          opacity="0.4"
                          d="M6.33332 8.3698C8.1697 8.3698 9.64174 6.87879 9.64174 5.01873C9.64174 3.15868 8.1697 1.66699 6.33332 1.66699C4.49694 1.66699 3.0249 3.15868 3.0249 5.01873C3.0249 6.87879 4.49694 8.3698 6.33332 8.3698Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                    <a className="relative inline-block text-left text-xs font-semibold text-white [text-decoration:none]">
                      Add
                    </a>
                  </button>
                  <div className="flex flex-col items-start justify-start px-0 pb-0 pt-0.5">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5 15.5L12 8.5L19 15.5"
                        stroke="black"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex max-w-full flex-col items-start justify-start self-stretch bg-white">
            <GridComponent
              dataSource={staff.filter((s) => s.is_deleted !== true)}
              editSettings={{
                allowEditing: true,
                allowAdding: true,
                allowDeleting: true,
                showDeleteConfirmDialog: true,
                mode: "Dialog",
              }}
              ref={gridStaff}
              actionBegin={actionBeginStaff}
              allowFiltering={true}
              filterSettings={filterSettings}
              allowSorting={true}
              sortSettings={sortSettings}
              allowPaging={true}
              pageSettings={pageSettings}
              showColumnMenu={true}
              columnMenuItems={columnMenuItems}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="staffId"
                  isPrimaryKey={true}
                  isIdentity={true}
                  visible={false}
                />
                <ColumnDirective
                  field="firstName"
                  headerText="First Name"
                  validationRules={{ required: true }}
                />
                <ColumnDirective
                  field="lastName"
                  headerText="Last Name"
                  validationRules={{ required: true }}
                />
                <ColumnDirective
                  field="email"
                  headerText="Email"
                  validationRules={{ required: true }}
                />
                <ColumnDirective
                  field="phoneNumber"
                  headerText="Phone Number"
                  validationRules={{ required: true }}
                />
                <ColumnDirective
                  headerText="Actions"
                  commands={[
                    {
                      type: "Edit",
                      buttonOption: {
                        content: '<i class="fas fa-edit"></i>',
                        cssClass: "e-outline custom-button",
                      },
                    },
                    {
                      type: "Delete",
                      buttonOption: {
                        content: '<i class="fas fa-trash-alt"></i>',
                        cssClass: "e-outline custom-button",
                      },
                    },
                  ]}
                />
              </ColumnsDirective>
              <Inject
                services={[Page, Sort, Filter, Edit, CommandColumn, ColumnMenu]}
              />
            </GridComponent>
          </div>
        </div>
      </div>

      <div className="box-border flex max-w-full flex-col items-start justify-start gap-[10px] self-stretch rounded-3xs border-[1px] border-solid border-gray-200 bg-white px-[19px] pb-5 pt-[18px] text-left text-xs text-darkslateblue">
        <div className="flex max-w-full flex-col items-start justify-start self-stretch bg-white">
          <div
            className={`mq280:flex-wrap mb-2 flex w-[1588px] max-w-full flex-row items-start justify-between gap-5 text-left text-xs leading-[normal] tracking-[normal] text-dodgerblue ${className}`}
          >
            <div
              className={`mq280:flex-wrap flex w-[1588px] max-w-full flex-row items-start justify-between gap-5 text-left text-xs leading-[normal] tracking-[normal] text-dodgerblue ${className}`}
            >
              <div className="flex w-full flex-col items-start justify-start">
                <a className="relative inline-block w-full font-bold leading-[21px] text-[inherit] [text-decoration:none]">
                  Local Government Contacts
                </a>
                <div className="relative self-stretch font-medium leading-[21px] text-silver-200">
                  {/* {data.lastUpdatedDisasters &&
                    `Last Updated: ${tempDisastersFormData.lastUpdatedDisasters}`}
                  {console.log("ahah ", data.lastUpdatedDisasters)} */}
                </div>
              </div>
              <div className="flex flex-col items-start justify-start px-0 pb-0 pt-[7px]">
                <div className="flex flex-row items-start justify-start gap-2.5">
                  <button
                    type="button"
                    className="btn-default flex cursor-pointer gap-1 px-3 py-1.5"
                    onClick={handleAddGov}
                  >
                    <div className="flex flex-col items-start justify-start px-0 pb-0 pt-px">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          opacity="0.4"
                          d="M14.0672 6.39191H13.2651V5.60774C13.2651 5.27296 12.9966 5 12.6658 5C12.3357 5 12.0665 5.27296 12.0665 5.60774V6.39191H11.2658C10.935 6.39191 10.6665 6.66487 10.6665 6.99965C10.6665 7.33443 10.935 7.6074 11.2658 7.6074H12.0665V8.39226C12.0665 8.72704 12.3357 9 12.6658 9C12.9966 9 13.2651 8.72704 13.2651 8.39226V7.6074H14.0672C14.3973 7.6074 14.6665 7.33443 14.6665 6.99965C14.6665 6.66487 14.3973 6.39191 14.0672 6.39191Z"
                          fill="white"
                        />
                        <path
                          d="M6.33325 10.0107C3.63607 10.0107 1.33325 10.442 1.33325 12.1648C1.33325 13.887 3.62205 14.3337 6.33325 14.3337C9.02977 14.3337 11.3333 13.9025 11.3333 12.1797C11.3333 10.4568 9.04445 10.0107 6.33325 10.0107Z"
                          fill="white"
                        />
                        <path
                          opacity="0.4"
                          d="M6.33332 8.3698C8.1697 8.3698 9.64174 6.87879 9.64174 5.01873C9.64174 3.15868 8.1697 1.66699 6.33332 1.66699C4.49694 1.66699 3.0249 3.15868 3.0249 5.01873C3.0249 6.87879 4.49694 8.3698 6.33332 8.3698Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                    <a className="relative my-auto inline-block text-left text-xs font-semibold text-white [text-decoration:none]">
                      Add
                    </a>
                  </button>
                  <div className="flex flex-col items-start justify-start px-0 pb-0 pt-0.5">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5 15.5L12 8.5L19 15.5"
                        stroke="black"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex max-w-full flex-col items-start justify-start self-stretch bg-white">
            <GridComponent
              dataSource={govContacts.filter((g) => g.is_deleted !== true)}
              editSettings={{
                allowEditing: true,
                allowAdding: true,
                allowDeleting: true,
                showDeleteConfirmDialog: true,
                mode: "Dialog",
              }}
              ref={govConactsRef}
              actionBegin={actionBeginGov}
              allowFiltering={true}
              filterSettings={filterSettings}
              allowSorting={true}
              sortSettings={sortSettings}
              allowPaging={true}
              pageSettings={pageSettings}
              showColumnMenu={true}
              columnMenuItems={columnMenuItems}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="govId"
                  isPrimaryKey={true}
                  isIdentity={true}
                  visible={false}
                />
                <ColumnDirective
                  field="firstName"
                  headerText="First Name"
                  validationRules={{ required: true }}
                />
                <ColumnDirective
                  field="lastName"
                  headerText="Last Name"
                  validationRules={{ required: true }}
                />
                <ColumnDirective
                  field="email"
                  headerText="Email"
                  validationRules={{ required: true }}
                />
                <ColumnDirective
                  field="phoneNumber"
                  headerText="Phone Number"
                  validationRules={{ required: true }}
                />
                <ColumnDirective
                  headerText="Actions"
                  commands={[
                    {
                      type: "Edit",
                      buttonOption: {
                        content: '<i class="fas fa-edit"></i>',
                        cssClass: "e-outline custom-button",
                      },
                    },
                    {
                      type: "Delete",
                      buttonOption: {
                        content: '<i class="fas fa-trash-alt"></i>',
                        cssClass: "e-outline custom-button",
                      },
                    },
                  ]}
                />
              </ColumnsDirective>
              <Inject
                services={[Page, Sort, Filter, Edit, CommandColumn, ColumnMenu]}
              />
            </GridComponent>
          </div>
        </div>
      </div>
      <DialogComponent
        width="400px"
        isModal={true}
        visible={showPopup}
        close={handleClosePopup}
        header={null}
        content={dialogContent}
        showCloseIcon={true}
        closeOnEscape={true}
        target={document.body}
        cssClass="welcome-dialog"
      />
    </form>
  );
};

CommunityProfile.propTypes = {
  className: PropTypes.string,
};

export default CommunityProfile;
